import React, { useState, useEffect } from 'react';
import { 
    Bell, 
    AlertTriangle, 
    ChevronRight,
    Filter,
    RefreshCw,
    CheckCircle,
    Search,
    Eye,
    UserPlus,
    X,
    TrendingUp,
    Copy,
    User,
    FileWarning,
    BarChart3
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { portfolioAPI } from '../../lib/api';


// Exception type icons
const ExceptionIcon = ({ type }) => {
    const icons = {
        expense_spike: TrendingUp,
        duplicate_invoice: Copy,
        unusual_vendor: User,
        vat_variance: FileWarning,
        pattern_deviation: BarChart3,
        vat_trend: TrendingUp,
    };
    const Icon = icons[type] || AlertTriangle;
    return <Icon className="w-4 h-4" />;
};

// Severity badge
const SeverityBadge = ({ severity }) => {
    const styles = {
        high: 'bg-red-500/10 text-red-400 border-red-500/20',
        medium: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
        low: 'bg-muted/40 text-muted-foreground border-border',
    };
    return (
        <span className={`px-2 py-0.5 text-xs rounded border ${styles[severity] || styles.medium}`}>
            {severity}
        </span>
    );
};

// Status badge
const StatusBadge = ({ status }) => {
    const styles = {
        open: 'bg-amber-500/10 text-amber-400',
        investigating: 'bg-blue-500/10 text-blue-400',
        assigned: 'bg-purple-500/10 text-purple-400',
        resolved: 'bg-emerald-500/10 text-emerald-400',
        dismissed: 'bg-muted/40 text-muted-foreground',
    };
    return (
        <span className={`px-2 py-0.5 text-xs rounded ${styles[status] || styles.open}`}>
            {status}
        </span>
    );
};

export default function ExceptionInbox() {
    const [loading, setLoading] = useState(true);
    const [exceptions, setExceptions] = useState([]);
    const [selectedException, setSelectedException] = useState(null);
    const [filters, setFilters] = useState({
        status: 'open',
        severity: '',
        type: ''
    });
    const [actionLoading, setActionLoading] = useState(false);

    const fetchExceptions = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.severity) params.append('severity', filters.severity);
            if (filters.type) params.append('exception_type', filters.type);
            
            const { data } = await portfolioAPI.exceptions(params.toString());
            setExceptions(data.exceptions || []);
        } catch (err) {
            console.error('Error fetching exceptions:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (exceptionId, action, assignedTo = null) => {
        setActionLoading(true);
        try {
            await portfolioAPI.actOnException(exceptionId, { action, assigned_to: assignedTo });
            toast.success(`Exception ${action === 'approve' ? 'approved' : action === 'investigate' ? 'marked for investigation' : action}`);
            await fetchExceptions();
            setSelectedException(null);
        } catch (err) {
            toast.error('Action failed');
        } finally {
            setActionLoading(false);
        }
    };

    useEffect(() => {
        fetchExceptions();
    }, [filters]);

    const formatAmount = (amount) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK' }).format(amount);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('da-DK');
    };

    return (
        <div className="min-h-screen bg-background text-foreground">
            {/* Header */}
            <div className="border-b border-border bg-background/95 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-muted border border-border rounded-lg flex items-center justify-center">
                                <Bell className="w-5 h-5 text-amber-500" />
                            </div>
                            <div>
                                <h1 className="font-medium text-foreground">Exception Inbox</h1>
                                <p className="text-xs text-muted-foreground">Transactions requiring review</p>
                            </div>
                        </div>
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={fetchExceptions}
                            className="border-border text-muted-foreground hover:text-foreground"
                        >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Refresh
                        </Button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Filters */}
                <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Filters:</span>
                    </div>
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters({...filters, status: e.target.value})}
                        className="bg-muted border border-border text-foreground text-sm rounded-lg px-3 py-2"
                    >
                        <option value="">All Status</option>
                        <option value="open">Open</option>
                        <option value="investigating">Investigating</option>
                        <option value="assigned">Assigned</option>
                        <option value="resolved">Resolved</option>
                        <option value="dismissed">Dismissed</option>
                    </select>
                    <select
                        value={filters.severity}
                        onChange={(e) => setFilters({...filters, severity: e.target.value})}
                        className="bg-muted border border-border text-foreground text-sm rounded-lg px-3 py-2"
                    >
                        <option value="">All Severity</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                    </select>
                    <select
                        value={filters.type}
                        onChange={(e) => setFilters({...filters, type: e.target.value})}
                        className="bg-muted border border-border text-foreground text-sm rounded-lg px-3 py-2"
                    >
                        <option value="">All Types</option>
                        <option value="expense_spike">Expense Spike</option>
                        <option value="duplicate_invoice">Duplicate Invoice</option>
                        <option value="unusual_vendor">Unusual Vendor</option>
                        <option value="vat_variance">VAT Variance</option>
                        <option value="pattern_deviation">Pattern Deviation</option>
                        <option value="vat_trend">VAT Trend</option>
                    </select>
                    <div className="ml-auto text-sm text-muted-foreground">
                        {exceptions.length} exception{exceptions.length !== 1 ? 's' : ''}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Exception List */}
                    <div className="space-y-3">
                        {loading ? (
                            <div className="text-muted-foreground text-center py-12">Loading exceptions...</div>
                        ) : exceptions.length === 0 ? (
                            <div className="bg-card border border-border rounded-xl p-12 text-center">
                                <CheckCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-foreground mb-2">Exception detection isn&rsquo;t available yet</h3>
                                <p className="text-muted-foreground max-w-md mx-auto">
                                    Nothing scans your clients&rsquo; data for exceptions yet, so this
                                    inbox stays empty. It previously showed sample exceptions, which
                                    have been removed. Real findings on your documents appear under{' '}
                                    <strong>Overview</strong>.
                                </p>
                            </div>
                        ) : (
                            exceptions.map((exception) => (
                                <div
                                    key={exception.id}
                                    onClick={() => setSelectedException(exception)}
                                    className={`bg-card border rounded-xl p-4 cursor-pointer transition-all ${
                                        selectedException?.id === exception.id 
                                            ? 'border-emerald-500/50 ring-1 ring-emerald-500/20' 
                                            : 'border-border hover:border-border'
                                    }`}
                                >
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                                exception.severity === 'high' ? 'bg-red-500/10 text-red-400' :
                                                exception.severity === 'medium' ? 'bg-amber-500/10 text-amber-400' :
                                                'bg-muted/40 text-muted-foreground'
                                            }`}>
                                                <ExceptionIcon type={exception.type} />
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium text-foreground">{exception.title}</div>
                                                <div className="text-xs text-muted-foreground">{exception.client_name}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <SeverityBadge severity={exception.severity} />
                                            <StatusBadge status={exception.status} />
                                        </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{exception.description}</p>
                                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                                        <span>{formatAmount(exception.amount)}</span>
                                        <span>{formatDate(exception.detected_at)}</span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Exception Detail Panel */}
                    <div className="lg:sticky lg:top-24 h-fit">
                        {selectedException ? (
                            <div className="bg-card border border-border rounded-xl overflow-hidden">
                                <div className="p-6 border-b border-border">
                                    <div className="flex items-start justify-between mb-4">
                                        <div>
                                            <h3 className="text-lg font-medium text-foreground">{selectedException.title}</h3>
                                            <p className="text-sm text-muted-foreground">{selectedException.client_name}</p>
                                        </div>
                                        <button onClick={() => setSelectedException(null)} className="text-muted-foreground hover:text-foreground">
                                            <X className="w-5 h-5" />
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <SeverityBadge severity={selectedException.severity} />
                                        <StatusBadge status={selectedException.status} />
                                    </div>
                                </div>
                                
                                <div className="p-6 space-y-6">
                                    {/* Transaction Details */}
                                    {selectedException.amount && (
                                        <div className="bg-muted/40 rounded-lg p-4 space-y-2">
                                            <div className="flex justify-between text-sm">
                                                <span className="text-muted-foreground">Amount</span>
                                                <span className="text-foreground font-medium">{formatAmount(selectedException.amount)}</span>
                                            </div>
                                            {selectedException.vendor && (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-muted-foreground">Vendor</span>
                                                    <span className="text-foreground">{selectedException.vendor}</span>
                                                </div>
                                            )}
                                            {selectedException.account_code && (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-muted-foreground">Account</span>
                                                    <span className="text-foreground">{selectedException.account_code} - {selectedException.account_name}</span>
                                                </div>
                                            )}
                                            {selectedException.transaction_date && (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-muted-foreground">Date</span>
                                                    <span className="text-foreground">{formatDate(selectedException.transaction_date)}</span>
                                                </div>
                                            )}
                                            {selectedException.historical_avg && (
                                                <div className="flex justify-between text-sm border-t border-border pt-2 mt-2">
                                                    <span className="text-muted-foreground">Historical Average</span>
                                                    <span className="text-foreground">{formatAmount(selectedException.historical_avg)}</span>
                                                </div>
                                            )}
                                            {selectedException.variance_percent && (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-muted-foreground">Variance</span>
                                                    <span className="text-red-400">+{selectedException.variance_percent.toFixed(0)}%</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    {/* Analysis */}
                                    <div>
                                        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Analysis</div>
                                        <p className="text-sm text-foreground leading-relaxed">{selectedException.description}</p>
                                    </div>
                                    
                                    {/* Actions */}
                                    {selectedException.status === 'open' && (
                                        <div className="flex gap-3 pt-4 border-t border-border">
                                            <Button
                                                size="sm"
                                                onClick={() => handleAction(selectedException.id, 'approve')}
                                                disabled={actionLoading}
                                                className="bg-emerald-600 hover:bg-emerald-500 flex-1"
                                            >
                                                <CheckCircle className="w-4 h-4 mr-2" />
                                                Approve
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleAction(selectedException.id, 'investigate')}
                                                disabled={actionLoading}
                                                className="border-border text-foreground flex-1"
                                            >
                                                <Eye className="w-4 h-4 mr-2" />
                                                Investigate
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleAction(selectedException.id, 'dismiss')}
                                                disabled={actionLoading}
                                                className="border-border text-foreground"
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-card border border-border rounded-xl p-12 text-center">
                                <Eye className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-foreground mb-2">Select an Exception</h3>
                                <p className="text-muted-foreground">Click on an exception to view details and take action.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
